import React, { useEffect, useState } from 'react';
import { Layout, Menu, Badge, Drawer } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  GiftOutlined,
  TeamOutlined,
  CalendarOutlined,
  FileProtectOutlined,
  ShoppingCartOutlined,
  FileAddOutlined,
  InboxOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  ToolOutlined,
  PieChartOutlined,
  UnorderedListOutlined,
  CloudUploadOutlined,
  SettingOutlined,
  ScheduleOutlined,
  SafetyOutlined,
  AuditOutlined,
  UserOutlined,
  HomeOutlined,
  ApartmentOutlined,
  DollarOutlined,
  DatabaseOutlined,
  MedicineBoxOutlined,
  ExperimentOutlined,
  SwapOutlined,
  ProfileOutlined,
  CheckSquareOutlined,
  UserAddOutlined,
  FlagOutlined,
  HistoryOutlined,
  EyeInvisibleOutlined,
  ShopOutlined,
  PrinterOutlined,
  DesktopOutlined,
  TagsOutlined,
  FileDoneOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import api from '../utils/api';
import { canSee, isAdmin, screensLoaded, serverMenu } from '../utils/roles';

const { Sider } = Layout;

/** Icons stay on the client; keys/labels/order come from backend/core/screens.py via /auth/screens/. */
const MENU_ICONS = {
  '/my-tasks': <CheckCircleOutlined />,
  '/purchase-requests': <FileAddOutlined />,
  '/horses': <UnorderedListOutlined />,
  '/horse-health': <MedicineBoxOutlined />,
  '/treatments': <ExperimentOutlined />,
  '/horse-movements': <SwapOutlined />,
  '/horse-activities': <ApartmentOutlined />,
  '/cashier-sales': <ShopOutlined />,
  '/clubsys-import': <CloudUploadOutlined />,
  '/timetable': <ScheduleOutlined />,
  '/gate': <SafetyOutlined />,
  '/pony-rides': <TagsOutlined />,
  '/school/day-end': <CheckSquareOutlined />,
  '/school/client-review': <UserAddOutlined />,
  '/school/compensations': <GiftOutlined />,
  '/coach-day': <UserOutlined />,
  '/school-control': <AuditOutlined />,
  '/livery': <HomeOutlined />,
  '/feed-transfer': <AppstoreOutlined />,
  '/feed-sheet': <CalendarOutlined />,
  '/feed-plan': <ToolOutlined />,
  '/bedding': <InboxOutlined />,
  '/feed-night-weigh': <FileProtectOutlined />,
  '/distribution': <AppstoreOutlined />,
  '/service-records': <ToolOutlined />,
  '/service-schedules': <CalendarOutlined />,
  '/lpos': <ShoppingCartOutlined />,
  '/goods-receipts': <InboxOutlined />,
  '/supplier-invoices': <FileDoneOutlined />,
  '/items': <TagsOutlined />,
  '/stock-control': <DatabaseOutlined />,
  '/reports': <BarChartOutlined />,
  '/horse-statistics': <PieChartOutlined />,
  '/horse-month-compare': <HistoryOutlined />,
  '/horse-cost-revenue': <DollarOutlined />,
  '/feed-variance-report': <PieChartOutlined />,
  '/service-cost-dashboard': <PieChartOutlined />,
  '/variance-report': <BarChartOutlined />,
  '/setup/horse-lists': <ProfileOutlined />,
  '/school-setup': <SettingOutlined />,
  '/school/opening': <FlagOutlined />,
  '/cost-centres': <ApartmentOutlined />,
  '/admin/core/user/': <TeamOutlined />,
  '/screens': <EyeInvisibleOutlined />,
  '/print-settings': <PrinterOutlined />,
  '/who-is-using': <DesktopOutlined />,
  '/admin/': <SettingOutlined />,
};

const ADMIN_GROUP = {
  key: 'admin',
  label: 'Users & Admin',
  adminOnly: true,
  children: [
    { key: '/admin/core/user/', label: 'Users & Roles' },
    { key: '/screens', label: 'Screens & Roles' },
    { key: '/print-settings', label: 'Print & Receipt' },
    { key: '/who-is-using', label: 'Who is using' },
    { key: '/admin/', label: 'System Admin' },
  ],
};

const buildGroups = () => {
  const fromServer = serverMenu();
  const groups = [];
  if (fromServer) {
    fromServer.forEach((g) => {
      const children = [];
      if (g.group === 'Home') {
        children.push({ key: '/my-tasks', label: 'My tasks', always: true });
      }
      (g.items || []).forEach((it) => children.push({ key: it.key, label: it.label }));
      groups.push({ key: g.group.toLowerCase().replace(/\s+/g, '-'), label: g.group, children });
    });
  }
  groups.push(ADMIN_GROUP);
  return groups;
};

const allowed = (item) => {
  if (item.adminOnly) return isAdmin();
  if (item.children || item.always) return true;
  return !screensLoaded() || canSee(item.key);
};

/** On phones the menu opens as a drawer (`open`/`onClose`); `footer` holds the account buttons there. */
const Sidebar = ({ mobile, open, onClose, footer }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [waiting, setWaiting] = useState(0);

  useEffect(() => {
    let live = true;
    const load = () => api.get('/auth/my-tasks/').then((r) => live && setWaiting(r.data.total)).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => { live = false; clearInterval(t); };
  }, [location.pathname]);

  const menuItems = buildGroups().filter((g) => allowed(g)).map((g) => {
    const children = g.children.filter((c) => allowed(c)).map((c) => {
      const icon = MENU_ICONS[c.key];
      const label = c.key === '/my-tasks'
        ? <span>{c.label} <Badge count={waiting} size="small" style={{ marginLeft: 4 }} /></span>
        : c.label;
      return { key: c.key, icon, label };
    });
    if (!children.length) return null;
    return { key: g.key, type: 'group', label: g.label, children };
  }).filter(Boolean);

  const path = location.pathname;
  const selected = path.startsWith('/horses/') ? '/horses' : (path.startsWith('/reports/') ? '/reports' : path);

  const menu = (
    <Menu
      mode="inline"
      selectedKeys={[selected]}
      style={{ borderRight: 0 }}
      items={menuItems}
      onClick={({ key }) => {
        if (key.startsWith('/admin/')) window.open(key, '_blank');
        else navigate(key);
        if (mobile) onClose();
      }}
    />
  );

  if (mobile) {
    return (
      <Drawer placement="left" open={open} onClose={onClose} width={270} title="ASHR" footer={footer}
        styles={{ body: { padding: 0 }, footer: { padding: 12 } }}>
        {menu}
      </Drawer>
    );
  }
  return (
    <Sider width={220} style={{ background: '#fff', overflowY: 'auto', height: '100vh', position: 'sticky', top: 0 }}>
      {menu}
    </Sider>
  );
};

export default Sidebar;
